import { useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { useEffect } from 'react';
import * as uuid from 'uuid';

import { SegmentedControl } from '../../cdl/SegmentedControl/SegmentedControl';
import type { SegmentedControlOption } from '../../cdl/SegmentedControl/SegmentedControl';
import { Tag } from '../../cdl/Tag/Tag';
import { Text } from '../../cdl/Text/Text';
import { ButtonGroup } from '../../common/buttons/ButtonGroup';
import { AdvancedError } from '../../common/Error/AdvancedError';
import { HeaderWithLine } from '../../common/HeaderWithLine/HeaderWithLine';
import { getOrderSourceHumanReadableValue } from '../../common/helpers/getOrderSourceHumanReadableValue.helper';
import { translate } from '../../common/helpers/translate.helper';
import { useDocumentTitle } from '../../common/hooks/useDocumentTitle';
import { useRole } from '../../common/hooks/useRole';
import { useSchedules } from '../../common/hooks/useSchedules';
import { useFlags } from '../../common/launchDarkly/useFlags';
import { LoadingIndicator } from '../../common/LoadingIndicator/LoadingIndicator';
import { Page } from '../../common/Page/Page';
import { ReadOnlyProductTable } from '../../common/ReadOnlyProductTable/ReadOnlyProductTable';
import { Box } from '../../common/ui/Box';
import { CompareOverlayFlow } from '../compare/overlay/CompareOverlayFlow';
import { OrderVesselInformation } from '../create/OrderVesselInformation';
import { OrderState } from '../model/OrderModel';

import { RequestSuppliersButton } from './common/RequestSuppliersButton';
import { EnquiredProductsAccordion } from './components/EnquiredProductsAccordion';
import { ReviewOffersButton } from './components/intelligence/ReviewOffersButton';
import { LineItemComparisonMatrix } from './components/LineItemComparison/LineItemComparisonMatrix';
import { OfferCompareActionBar } from './components/OfferCompareActionBar';
import { OfferList } from './components/OfferList';
import { OrderDetails } from './components/OrderDetails';
import { OrderStateTag } from './components/OrderStateTag';
import { useOrderDetails } from './hooks/useOrderDetails';
import { useOrderOffers } from './hooks/useOrderOffers';
import { OfferCompareView } from './OfferCompareView';
import type { OfferCompareViewValue } from './OfferCompareView';

export const OfferComparePage = () => {
    const { id } = useParams({ from: '/_app/order/$id' });
    const search = useSearch({ from: '/_app/order/$id' });
    const role = useRole();
    const flags = useFlags();
    const navigate = useNavigate();

    const orderQuery = useOrderDetails(id);

    const order = orderQuery.data;
    const offersQuery = useOrderOffers(order);
    const { schedules: schedule, isPending: isLoadingSchedules } = useSchedules({ imo: order?.vessel?.imo });

    useDocumentTitle(translate('page.ordershow', { orderNumber: order?.orderNumber ?? '' }));

    // Redirect customers viewing draft orders to the draft enquiry page
    useEffect(() => {
        if (order?.isDraft() && role.isCustomer()) {
            navigate({
                to: '/orders/draft/$id',
                params: { id: order.id },
                replace: true,
            });
        }
    }, [order, role, navigate]);

    if (orderQuery.isPending) {
        return (
            <Page>
                <LoadingIndicator variant="page" />
            </Page>
        );
    }

    if (orderQuery.isError || !order) {
        return (
            <Page>
                <AdvancedError />
            </Page>
        );
    }

    const orderSource = getOrderSourceHumanReadableValue(order.source);
    const canCompareLineItems =
        (role.isSuperAdmin() || (flags.enableLineItemComparisonMatrix && role.isCustomer())) &&
        (offersQuery.data?.length ?? 0) >= 2;
    const offerDetailViewOptions: SegmentedControlOption<OfferCompareViewValue>[] = [
        { value: OfferCompareView.OVERVIEW, label: translate('order.detail.offerComparison.overviewTab') },
        { value: OfferCompareView.LINE_ITEMS, label: translate('order.detail.lineItemComparison.productsTab') },
    ];
    const selectedOfferDetailView = canCompareLineItems ? search.view : OfferCompareView.OVERVIEW;

    return (
        <Page>
            <Box display="grid" rowGap={24}>
                <HeaderWithLine>
                    <Box display="flex" flexDirection="row" gap={8} alignItems="baseline">
                        <Text variant="headline" as="h1">
                            {translate('order.detail.headline')}
                        </Text>
                        <Box display="flex" flexDirection="row" gap={8} alignItems="center">
                            <Text variant="title" color="foreground.subtle">
                                {order.orderNumber}
                            </Text>

                            <OrderStateTag state={order.state} />

                            {orderSource ? <Tag variant="accent">{orderSource}</Tag> : null}
                        </Box>
                    </Box>

                    <OfferCompareActionBar order={order} />
                </HeaderWithLine>

                <Box display="grid" gridTemplateColumns="1fr 1fr" gap={40}>
                    <div>
                        <OrderDetails order={order} />
                    </div>
                    <OrderVesselInformation vesselId={order.vesselId} />
                </Box>

                {order.isDraft() && (order.items.length || order.samplekits.length) ? (
                    <ReadOnlyProductTable items={order.items} sampleKits={order.samplekits} />
                ) : null}

                {!order.isDraft() ? (
                    <EnquiredProductsAccordion items={order.items} sampleKits={order.samplekits} />
                ) : null}

                {role.isSuperAdmin() && order.state !== OrderState.DRAFT && !isLoadingSchedules ? (
                    // @ts-ignore
                    <CompareOverlayFlow trackingId={uuid.v4()} order={order} schedules={schedule} />
                ) : null}

                <Box display="grid" rowGap={16} marginTop={24}>
                    <HeaderWithLine>
                        <Box display="flex" gap={12} alignItems="center">
                            <Text variant="title" as="h2">
                                {translate('order.detail.offers')}
                            </Text>

                            {canCompareLineItems ? (
                                <SegmentedControl
                                    options={offerDetailViewOptions}
                                    value={selectedOfferDetailView}
                                    onChange={(view) =>
                                        navigate({
                                            from: '/order/$id',
                                            search: (previousSearch) => ({ ...previousSearch, view }),
                                        })
                                    }
                                />
                            ) : null}
                        </Box>

                        <ButtonGroup>
                            {flags.closelinkIntelligence ? <ReviewOffersButton order={order} /> : null}
                            <RequestSuppliersButton order={order} />
                        </ButtonGroup>
                    </HeaderWithLine>

                    {selectedOfferDetailView === OfferCompareView.LINE_ITEMS ? (
                        <LineItemComparisonMatrix order={order} />
                    ) : (
                        <OfferList order={order} />
                    )}
                </Box>
            </Box>
        </Page>
    );
};
